<template>
    <div class="test-results">
        <div
            class="test-result-category"
            v-if="testResultKey === 'artistic-conventional'">
            <h2 class="title">Jij bent artistiek & conventioneel</h2>
            <p class="description">
                Met artistiek wordt bedoeld dat je fantasierijk bent, dingen op een originele manier bekijkt, van
                vormgeving houdt en creatief bent. Met conventioneel wordt bedoeld dat je accuraat en betrouwbaar bent
                en gestructureerd werkt. Een hoge score op artistiek en conventioneel is gewenst bij studies waarmee je
                bijvoorbeeld een proeflezer, redacteur, grafisch vormgever, webdesigner, visual designer of 3D
                printspecialist kunt worden. Dit zijn allemaal beroepen voor mensen die gestructureerd en creatief zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen zitten studies die vaak hoog scoren op artistiek en conventioneel.
            </p>
            <ul>
                <li>Techniek (onder andere Industrieel Product Ontwerpen)</li>
                <li>Taal en Communicatie (onder andere Communication and Multimedia Design, Taalwetenschappen)</li>
                <li>Kunst en Cultuur (onder andere Vormgeving, Mediastudies)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'artistic-enterprising'">
            <h2 class="title">Jij bent artistiek & ondernemend</h2>
            <p class="description">
                Met artistiek wordt bedoeld dat je fantasierijk bent, dingen op een originele manier bekijkt, van
                vormgeving houdt en creatief bent. Met ondernemend wordt bedoeld dat je initiatiefrijk bent, graag
                verantwoordelijkheid draagt en overtuigend kunt zijn. Een hoge score op artistiek en ondernemend is
                gewenst bij studies waarmee je bijvoorbeeld een industrieel ontwerper, interieurarchitect, copywriter,
                regisseur, illustrator, producer of entrepreneur kunt worden. Dit zijn allemaal beroepen voor mensen die
                initiatiefrijk en creatief zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij vier van deze
                studierichtingen worden studies aangeboden die vaak hoog scoren op artistiek en ondernemend.
            </p>
            <ul>
                <li>Techniek (onder andere Industrial Design)</li>
                <li>Taal en Communicatie (onder andere Theater, Communicatie- en Informatiewetenschappen)</li>
                <li>Kunst en Cultuur (onder andere Vormgeving, Mediastudies)</li>
                <li>Economie en Bedrijf (onder andere Business Innovation)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'artistic-intellectual'">
            <h2 class="title">Jij bent intellectueel & artistiek</h2>
            <p class="description">
                Met intellectueel wordt bedoeld dat je onderzoekend bent ingesteld. Je vraagt je bijvoorbeeld af hoe
                dingen werken en bent leergierig. Met artistiek wordt bedoeld dat je fantasierijk bent, dingen op een
                originele manier bekijkt, van vormgeving houdt en creatief bent. Een hoge score op intellectueel en
                artistiek is gewenst bij studies waarmee je bijvoorbeeld auteur, architect, motion designer, desktop
                publisher, antropoloog of trendwatcher kunt worden. Dit zijn allemaal beroepen voor mensen die creatief
                en onderzoekend zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij veel van deze
                studierichtingen zitten studies die hoog scoren op creativiteit en onderzoek. Denk daarbij aan studies
                op het gebied van taal, ontwerp of cultuur. Deze kan je vinden bij onderstaande vier studierichtingen.
            </p>
            <ul>
                <li>Kunst & Cultuur (Vormgeving)</li>
                <li>Techniek (Architecture)</li>
                <li>Taal & Communicatie (Franse Taal en Cultuur)</li>
                <li>Gedrag en Maatschappij (Culturele Antropologie)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'artistic-realistic'">
            <h2 class="title">Jij bent realistisch & artistiek</h2>
            <p class="description">
                Met realistisch wordt bedoeld dat je graag met je handen werkt of het leuk vindt om apparatuur te
                bedienen en dat je niet goed kan stilzitten. Realistisch werk is vaak het tegenovergestelde van een
                kantoorbaan. Met artistiek wordt bedoeld dat je fantasierijk bent, dingen op een originele manier
                bekijkt, van vormgeving houdt en creatief bent.
            </p>
            <p>
                Een hoge score op realistisch en artistiek is gewenst bij studies waarmee je bijvoorbeeld beeldend
                kunstenaar, acteur, architect of industrieel ontwerper kunt worden. Dit zijn allemaal beroepen voor
                mensen die creatief en praktisch zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>

            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij twee van deze
                studierichtingen zitten studies die vaak hoog scoren op realistisch en artistiek.
            </p>

            <ul>
                <li>Kunst & Cultuur (onder andere Autonome Beeldende Kunst, Theater)</li>
                <li>Techniek (onder andere Architecture, Industrial Design)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'artistic-social'">
            <h2 class="title">Jij bent artistiek & sociaal</h2>
            <p class="description">
                Met artistiek wordt bedoeld dat je fantasierijk bent, dingen op een originele manier bekijkt, van
                vormgeving houdt en creatief bent. Met sociaal wordt bedoeld dat je mensgericht bent, graag andere
                mensen helpt of het leuk vindt om anderen te inspireren. Een hoge score op artistiek en sociaal is
                gewenst bij studies waarmee je bijvoorbeeld een leraar basisonderwijs, kunstdocent, muziekdocent,
                remedial teacher, tolk en vertaler kunt worden. Dit zijn allemaal beroepen voor mensen die hulpvaardig
                en creatief zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen worden studies aangeboden die vaak hoog scoren op artistiek en sociaal.
            </p>
            <ul>
                <li>Kunst en Cultuur (onder andere Kunsteducatie, Muziek)</li>
                <li>Onderwijs en Opvoeding (onder andere Lerarenopleiding Basisonderwijs, Pedagogiek)</li>
                <li>Taal en Communicatie (onder andere Vertaler, Engelse Taal en Cultuur)</li>
            </ul>
            <p>
                Achter elke studierichting staan een paar voorbeeldstudies. Het aanbod van studies is nog veel groter.
                Nu weet je beter bij welke studierichtingen de kans het grootst is op een studie die jou aanspreekt.
            </p>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'conventional-enterprising'">
            <h2 class="title">Jij bent ondernemend & conventioneel</h2>
            <p class="description">
                Met ondernemend wordt bedoeld dat je initiatiefrijk bent, graag verantwoordelijkheid draagt en
                overtuigend kunt zijn. Met conventioneel wordt bedoeld dat je accuraat en betrouwbaar bent en
                gestructureerd werkt. Een hoge score op ondernemend en conventioneel is gewenst bij studies waarmee je
                bijvoorbeeld een accountant, notaris, intercedent, taxateur of manager kunt worden. Dit zijn allemaal
                beroepen voor mensen die initiatiefrijk en gestructureerd zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen zitten studies die vaak hoog scoren op ondernemend en conventioneel.
            </p>
            <ul>
                <li>Economie en Bedrijf (onder andere Accountancy, Vastgoed en Makelaardij, Bedrijfskunde)</li>
                <li>Gedrag en Maatschappij (onder andere Human Resource Management)</li>
                <li>Recht en Bestuur (onder andere Rechtsgeleerdheid, Overheidsmanagement)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'conventional-intellectual'">
            <h2 class="title">Jij bent intellectueel & conventioneel</h2>
            <p class="description">
                Met intellectueel wordt bedoeld dat je onderzoekend bent ingesteld. Je vraagt je bijvoorbeeld af hoe
                dingen werken en bent leergierig. Met conventioneel wordt bedoeld dat je accuraat en betrouwbaar bent en
                gestructureerd werkt. Een hoge score op intellectueel en conventioneel is gewenst bij studies waarmee je
                bijvoorbeeld een software developer, software engineer, jurist, beleidsmedewerker, financieel analist of
                actuaris kunt worden. Dit zijn allemaal beroepen voor mensen die onderzoekend en zorgvuldig zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen zitten studies die vaak hoog scoren op intellectueel en conventioneel.
            </p>
            <ul>
                <li>Exact en Informatica (onder andere Artificial Intelligence, Informatica)</li>
                <li>Recht en Bestuur (onder andere Rechtsgeleerdheid, Bestuurskunde)</li>
                <li>Economie en Bedrijf (onder andere Finance, Econometrie)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'conventional-realistic'">
            <h2 class="title">Jij bent realistisch & conventioneel</h2>
            <p class="description">
                Met realistisch wordt bedoeld dat je graag met je handen werkt of het leuk vindt om apparatuur te
                bedienen en dat je niet goed kan stilzitten. Realistisch werk is vaak het tegenovergestelde van een
                kantoorbaan. Met conventioneel wordt bedoeld dat je accuraat en betrouwbaar bent en gestructureerd
                werkt.
            </p>
            <p>
                Een hoge score op realistisch en conventioneel is gewenst bij studies waarmee je bijvoorbeeld een
                maritiem officier, bouwkundig tekenaar, landmeter, voedselinspecteur, transportinspecteur of
                schadebehandelaar kunt worden. Dit zijn allemaal beroepen voor mensen die praktisch en zorgvuldig zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen zitten studies die vaak hoog scoren op realistisch en conventioneel.
            </p>
            <ul>
                <li>
                    Aarde & Milieu (onder andere Voedingsmiddelentechnologie, Tuinbouw en Akkerbouw, Landscape and
                    Environment Management)
                </li>
                <li>Techniek (onder andere Maritiem Officier, Logistics Management, Bouwkunde)</li>
                <li>Economie en Bedrijf (onder andere Commerciële Economie, Technische Bedrijfskunde)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'conventional-social'">
            <h2 class="title">Jij bent sociaal & conventioneel</h2>
            <p class="description">
                Met sociaal wordt bedoeld dat je mensgericht bent, graag andere mensen helpt of het leuk vindt om
                anderen te inspireren. Met conventioneel wordt bedoeld dat je accuraat en betrouwbaar bent en
                gestructureerd werkt. Een hoge score op sociaal en conventioneel is gewenst bij studies waarmee je
                bijvoorbeeld een physician assistant, operatieassistent, zorgcoördinator, WMO-consulent, maatschappelijk
                werker of onderwijsbestuurder kunt worden. Dit zijn allemaal beroepen voor mensen die mensgericht en
                gestructureerd zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij twee van deze
                studierichtingen zitten studies die vaak hoog scoren op sociaal en conventioneel.
            </p>
            <ul>
                <li>Gezondheid (onder andere Physician Assistant, Verpleegkundige)</li>
                <li>
                    Gedrag en Maatschappij (onder andere Sociaal-Juridische Dienstverlening, Human Resource Management)
                </li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'enterprising-intellectual'">
            <h2 class="title">Jij bent intellectueel & ondernemend</h2>
            <p class="description">
                Met intellectueel wordt bedoeld dat je onderzoekend bent ingesteld. Je vraagt je bijvoorbeeld af hoe
                dingen werken en bent leergierig. Met ondernemend wordt bedoeld dat je initiatiefrijk bent, graag
                verantwoordelijkheid draagt en overtuigend kunt zijn. Een hoge score op intellectueel en ondernemend is
                gewenst bij studies waarmee je bijvoorbeeld een marketingconsultant, organisatieadviseur,
                beleidsmedewerker, advocaat, data scientist of IT-consultant kunt worden. Dit zijn allemaal beroepen
                voor mensen die initiatiefrijk en onderzoekend zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen zitten studies die vaak hoog scoren op intellectueel en ondernemend.
            </p>
            <ul>
                <li>Exact en Informatica (onder andere Toegepaste wiskunde, Informatica)</li>
                <li>Recht en Bestuur (onder andere Rechtsgeleerdheid, Bestuurskunde)</li>
                <li>Economie en Bedrijf (onder andere Bedrijfskunde, Commerciële Economie)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'enterprising-realistic'">
            <h2 class="title">Jij bent realistisch & ondernemend</h2>
            <p class="description">
                Met realistisch wordt bedoeld dat je graag met je handen werkt of het leuk vindt om apparatuur te
                bedienen en dat je niet goed kan stilzitten. Realistisch werk is vaak het tegenovergestelde van een
                kantoorbaan. Met ondernemend wordt bedoeld dat je initiatiefrijk bent, graag verantwoordelijkheid draagt
                en overtuigend kunt zijn.
            </p>
            <p>
                Een hoge score op realistisch en ondernemend is gewenst bij studies waarmee je bijvoorbeeld een
                agrarisch ondernemer, teeltmanager, bouwmanager, quality engineer of facility manager kunt worden. Dit
                zijn allemaal beroepen voor mensen die initiatiefrijk en praktisch zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen zitten studies die vaak hoog scoren op realistisch en ondernemend.
            </p>
            <ul>
                <li>Aarde & Milieu (onder andere Tuinbouw en Akkerbouw, Dier- en Veehouderij)</li>
                <li>Techniek (onder andere Civiele Techniek, Bouwkunde)</li>
                <li>Economie en Bedrijf (onder andere Facility Management, Bedrijfskunde)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'enterprising-social'">
            <h2 class="title">Jij bent sociaal & ondernemend</h2>
            <p class="description">
                Met sociaal wordt bedoeld dat je mensgericht bent, graag andere mensen helpt of het leuk vindt om
                anderen te inspireren. Met ondernemend wordt bedoeld dat je initiatiefrijk bent, graag
                verantwoordelijkheid draagt en overtuigend kunt zijn. Een hoge score op sociaal en ondernemend is
                gewenst bij studies waarmee je bijvoorbeeld een mediator, orthopedagoog, accountmanager, docent
                voortgezet onderwijs, rechter of HR-manager kunt worden. Dit zijn allemaal beroepen voor mensen die
                mensgericht en initiatiefrijk zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij vier van deze
                studierichtingen worden studies aangeboden die vaak hoog scoren op sociaal en ondernemend.
            </p>
            <ul>
                <li>Recht en Bestuur (onder andere Rechtsgeleerdheid)</li>
                <li>Economie en Bedrijf (onder andere Commerciële Economie)</li>
                <li>Onderwijs en Opvoeding (onder andere Pedagogiek, Lerarenopleiding)</li>
                <li>Gedrag en Maatschappij (onder andere Human Resource Management)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'intellectual-realistic'">
            <h2 class="title">Jij bent realistisch & intellectueel</h2>
            <p class="description">
                Met realistisch wordt bedoeld dat je graag met je handen werkt of het leuk vindt om apparatuur te
                bedienen en dat je niet goed kan stilzitten. Realistisch werk is vaak het tegenovergestelde van een
                kantoorbaan. Met intellectueel wordt bedoeld dat je onderzoekend bent ingesteld. Je vraagt je
                bijvoorbeeld af hoe dingen werken en bent leergierig.
            </p>
            <p>
                Een hoge score op realistisch en intellectueel is gewenst bij studies waarmee je bijvoorbeeld een
                teeltspecialist, planoloog, elektro engineer, werktuigbouwkundige, fysiotherapeut of cardioloog kunt
                worden. Dit zijn allemaal beroepen voor mensen die analytisch en praktisch zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen zitten studies die vaak hoog scoren op realistisch en intellectueel.
            </p>
            <ul>
                <li>Aarde & Milieu (Tuinbouw en Akkerbouw of Planologie)</li>
                <li>Techniek (Elektrotechniek of Werktuigbouwkunde)</li>
                <li>Gezondheid (Fysiotherapie of Geneeskunde)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'intellectual-social'">
            <h2 class="title">Jij bent intellectueel & sociaal</h2>
            <p class="description">
                Met intellectueel wordt bedoeld dat je onderzoekend bent ingesteld. Je vraagt je bijvoorbeeld af hoe
                dingen werken en bent leergierig. Met sociaal wordt bedoeld dat je mensgericht bent, graag andere mensen
                helpt of het leuk vindt om anderen te inspireren. Een hoge score op intellectueel en sociaal is gewenst
                bij studies waarmee je bijvoorbeeld een huisarts, internist, diëtist, psycholoog, pedagoog of
                re-integratiecoach kunt worden. Dit zijn allemaal beroepen voor mensen die hulpvaardig en onderzoekend
                zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij drie van deze
                studierichtingen zitten studies die vaak hoog scoren op intellectueel en sociaal.
            </p>
            <ul>
                <li>Gezondheid (onder andere Voeding en Diëtetiek en Geneeskunde)</li>
                <li>Onderwijs en Opvoeding (onder andere Pedagogiek)</li>
                <li>Gedrag en Maatschappij (onder andere Psychologie)</li>
            </ul>
        </div>

        <div
            class="test-result-category"
            v-if="testResultKey === 'realistic-social'">
            <h2 class="title">Jij bent realistisch & sociaal</h2>
            <p class="description">
                Met realistisch wordt bedoeld dat je graag met je handen werkt of het leuk vindt om apparatuur te
                bedienen en dat je niet goed kan stilzitten. Realistisch werk is vaak het tegenovergestelde van een
                kantoorbaan. Met sociaal wordt bedoeld dat je mensgericht bent, graag andere mensen helpt of het leuk
                vindt om anderen te inspireren.
            </p>
            <p>
                Een hoge score op realistisch en sociaal is gewenst bij studies waarmee je bijvoorbeeld een gymdocent,
                docent techniek, verloskundige, mondhygiënist of kaakchirurg kunt worden. Dit zijn allemaal beroepen
                voor mensen die hulpvaardig en praktisch zijn.
            </p>

            <design-2021-apprentice-radar-chart-component
                class="my-4"
                :options="chartOptions"
                :series="series">
            </design-2021-apprentice-radar-chart-component>

            <h3 class="sub-title">Passende studierichtingen</h3>
            <p>
                Alle studies in Nederland kan je onderverdelen in 10 studierichtingen. Bij twee van deze
                studierichtingen zitten studies die vaak hoog scoren op realistisch en sociaal.
            </p>
            <ul>
                <li>Gezondheid (onder andere Mondzorgkunde, Verloskunde, Geneeskunde)</li>
                <li>
                    Onderwijs en Opvoeding (onder andere Opleiding tot Leraar Lichamelijke Opvoeding, Opleiding tot
                    Leraar Techniek)
                </li>
            </ul>
        </div>

        <!-- Always show: -->
        <div class="disclaimer">
            <p>
                Achter elke studierichting staan een paar voorbeeldstudies. Het aanbod van studies is nog veel groter.
                Nu weet je beter bij welke studierichtingen de kans het grootst is op een studie die jou aanspreekt.
            </p>
            <h3 class="sub-title mt-4">Kies een studie die je leuk vindt</h3>
            <p>
                Als je op zoek bent naar een passende studie moet je vooral onderzoeken wat je leuk vindt. Zeker als je
                jong bent. Dan hoef je nog niet te kijken naar je competenties. Het idee van een studie is nu juist dat
                je bepaalde competenties gaat leren. De kans dat je een studie leuk vindt, is het grootst als deze
                aansluit bij jouw interesses en persoonlijkheid. Mensen die van nature heel sociaal zijn en graag
                anderen helpen, kiezen waarschijnlijk een andere studie dan mensen die van denksporten houden en meer op
                zichzelf zijn. Met behulp van de studiekeuzetest weet je beter wat voor soort studies aansluiten bij
                jouw interesses.
            </p>
        </div>
    </div>
</template>

<script>
import { getAttributesForKeys, sortResults } from '../../../../helpers/testCenter';

const testKey = 'job-personality-study-choice-test-2024-05-16';

export default {
    props: {
        resultData: {
            type: Object,
        },
    },
    data() {
        return {
            series: [
                {
                    color: '#07A1FF',
                    data: sortResults(testKey, this.resultData),
                    name: '',
                },
            ],
            chartOptions: {
                chart: {
                    height: 350,
                    width: '100%',
                    type: 'radar',
                    toolbar: {
                        tools: {
                            download: false,
                        },
                    },
                },
                plotOptions: {
                    radar: {
                        size: 140,
                        polygons: {
                            strokeColors: '#e9e9e9',
                            fill: {
                                colors: ['#f8f8f8', '#fff'],
                            },
                        },
                    },
                },
                xaxis: {
                    categories: getAttributesForKeys(testKey, 'label'),
                },
                yaxis: {
                    show: false,
                },
                tooltip: {
                    enabled: false,
                },
            },
            testResultKey: this.determineResultKey(this.resultData),
        };
    },
    methods: {
        determineResultKey(resultData) {
            /**
             * Het kan voorkomen dat iemands hoogste score op drie kenmerken even hoog is.
             * Bijvoorbeeld 6 – 6 – 6 – 5 – 4 – 3. Of dat iemand zijn/haar score op de tweede en derde kenmerk even hoog is.
             * Bijvoorbeeld 5 – 6 – 6 – 8 – 3 – 2. In dat geval moet er een keuze gemaakt worden. Want op basis van iemands
             * twee hoogste scores wordt bepaald welke rapportagetekst wordt getoond.
             * Volgorde uitslag bij gelijke score: R<C<O<S<A<I. Deze volgorde is gebaseerd op voldoende passende en
             * kansrijke beroepen wat leidt tot studies met een grotere kans op werk.
             */
            const importanceOrder = ['realistic', 'conventional', 'enterprising', 'social', 'artistic', 'intellectual'];
            return (
                Object.keys(resultData)
                    // Sort the keys by score and importance
                    .sort((a, b) => {
                        const scoreDifference = resultData[b] - resultData[a];
                        if (scoreDifference !== 0) return scoreDifference;
                        return importanceOrder.indexOf(a) - importanceOrder.indexOf(b);
                    })
                    // Take the two highest scores
                    .slice(0, 2)
                    // Sort the two keys alphabetically to make the key deterministic
                    .sort()
                    .join('-')
            );
        },
    },
};
</script>

<style scoped lang="scss">
.description {
    margin-bottom: map-get($grid-gutter-spacing, 'twenty-one');
}
</style>
